// src/components/DashboardMapView.jsx
import { useEffect, useMemo, useRef } from "react";
import mapboxgl from "mapbox-gl";
import "mapbox-gl/dist/mapbox-gl.css";
import { addTerrainLayer, SATELLITE_STYLE } from "../utils/mapTerrain.js";

/**
 * Fixes:
 * ✅ Popup not clipped (rounded canvas, no parent overflow hidden)
 * ✅ Button "Відкрити поле" works reliably (bind after addTo)
 * ✅ centroid for Polygon/MultiPolygon
 */
export default function DashboardMapView({
  height = 620,
  token,
  mode = "CROP",
  fields = [],
  selectedId,
  showAll = false,
  onSelect,
  onOpenField,
}) {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const popupRef = useRef(null);

  const onSelectRef = useRef(onSelect);
  const onOpenFieldRef = useRef(onOpenField);

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    onOpenFieldRef.current = onOpenField;
  }, [onOpenField]);

  const safeJsonParse = (s) => {
    if (!s || typeof s !== "string") return null;
    try {
      return JSON.parse(s);
    } catch {
      return null;
    }
  };

  const toGeometry = (obj) => {
    if (!obj) return null;
    const parsed = typeof obj === "string" ? safeJsonParse(obj) : obj;
    if (!parsed || !parsed.type) return null;

    if (
      parsed.type === "Polygon" ||
      parsed.type === "MultiPolygon" ||
      parsed.type === "LineString" ||
      parsed.type === "MultiLineString" ||
      parsed.type === "Point" ||
      parsed.type === "MultiPoint" ||
      parsed.type === "GeometryCollection"
    ) {
      return parsed;
    }
    if (parsed.type === "Feature") return parsed.geometry?.type ? parsed.geometry : null;
    if (parsed.type === "FeatureCollection") {
      const first = Array.isArray(parsed.features) ? parsed.features[0] : null;
      return first?.geometry?.type ? first.geometry : null;
    }
    return null;
  };

  const extractGeometry = (row) => {
    const raw =
      row?.geometryJson ??
      row?.geometry_json ??
      row?.geometry ??
      row?.geometryGeoJson ??
      row?.geoJson ??
      null;
    return toGeometry(raw);
  };

  const walkCoords = (coords, cb) => {
    if (!coords) return;
    if (typeof coords[0] === "number" && typeof coords[1] === "number") {
      cb(coords);
      return;
    }
    for (const c of coords) walkCoords(c, cb);
  };

  const computeBoundsFromGeom = (geom) => {
    if (!geom) return null;
    const b = new mapboxgl.LngLatBounds();
    let ok = false;

    const eat = (coords) => {
      walkCoords(coords, (c) => {
        b.extend(c);
        ok = true;
      });
    };

    if (geom.type === "GeometryCollection" && Array.isArray(geom.geometries)) {
      for (const g of geom.geometries) if (g?.coordinates) eat(g.coordinates);
      return ok ? b : null;
    }

    if (!geom.coordinates) return null;
    eat(geom.coordinates);
    return ok ? b : null;
  };

  // centroid
  function ringCentroid(ring) {
    if (!Array.isArray(ring) || ring.length < 3) return null;

    const pts =
      ring[0][0] === ring[ring.length - 1][0] && ring[0][1] === ring[ring.length - 1][1]
        ? ring
        : [...ring, ring[0]];

    let area2 = 0;
    let cx = 0;
    let cy = 0;

    for (let i = 0; i < pts.length - 1; i++) {
      const [x1, y1] = pts[i];
      const [x2, y2] = pts[i + 1];
      const a = x1 * y2 - x2 * y1;
      area2 += a;
      cx += (x1 + x2) * a;
      cy += (y1 + y2) * a;
    }

    if (!Number.isFinite(area2) || Math.abs(area2) < 1e-12) return null;

    const area6 = 3 * area2;
    cx /= area6;
    cy /= area6;

    if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
    return [cx, cy];
  }

  function polygonCentroid(coords) {
    if (!Array.isArray(coords) || !Array.isArray(coords[0])) return null;
    return ringCentroid(coords[0]);
  }

  function multiPolygonCentroid(coords) {
    if (!Array.isArray(coords) || !coords.length) return null;

    let best = null;
    let bestAbs = -1;

    for (const poly of coords) {
      const c = polygonCentroid(poly);
      if (!c) continue;

      const outer = Array.isArray(poly) ? poly[0] : null;
      if (!outer || outer.length < 3) continue;

      const pts =
        outer[0][0] === outer[outer.length - 1][0] && outer[0][1] === outer[outer.length - 1][1]
          ? outer
          : [...outer, outer[0]];

      let area2 = 0;
      for (let i = 0; i < pts.length - 1; i++) {
        const [x1, y1] = pts[i];
        const [x2, y2] = pts[i + 1];
        area2 += x1 * y2 - x2 * y1;
      }

      const abs = Math.abs(area2);
      if (abs > bestAbs) {
        bestAbs = abs;
        best = c;
      }
    }
    return best;
  }

  // Середнє арифметичне вершин — завжди всередині опуклого полігону,
  // і значно точніше bbox-центру для витягнутих/трикутних форм
  function vertexMean(geom) {
    const pts = [];
    const walk = (c) => {
      if (!c) return;
      if (typeof c[0] === "number") { pts.push(c); return; }
      for (const x of c) walk(x);
    };
    walk(geom.coordinates ?? geom.geometries?.flatMap?.(g => g.coordinates));
    if (!pts.length) return null;
    return [
      pts.reduce((s, p) => s + p[0], 0) / pts.length,
      pts.reduce((s, p) => s + p[1], 0) / pts.length,
    ];
  }

  const computeCenterFromGeom = (geom) => {
    try {
      if (!geom) return null;
      if (geom.type === "Polygon") {
        const c = polygonCentroid(geom.coordinates);
        if (c) return c;
      }
      if (geom.type === "MultiPolygon") {
        const c = multiPolygonCentroid(geom.coordinates);
        if (c) return c;
      }
      // fallback: vertex average — краще за bbox-центр для нерівних форм
      return vertexMean(geom);
    } catch {
      return null;
    }
  };

  const esc = (str) =>
    String(str ?? "")
      .replaceAll("&", "&amp;")
      .replaceAll("<", "&lt;")
      .replaceAll(">", "&gt;")
      .replaceAll('"', "&quot;")
      .replaceAll("'", "&#039;");

  const fmtFixed = (n, d = 2) => {
    const x = Number(n);
    if (!Number.isFinite(x)) return "—";
    return x.toFixed(d);
  };

  const cropImgFromProps = (p) => {
    const id = p?.cropId != null ? String(p.cropId) : "";
    return id ? `/crops/crop-${id}.png` : "";
  };

  const toneRisk = (level) => {
    const x = String(level || "").toUpperCase();
    if (x === "HIGH") return { text: "HIGH", bd: "rgba(239,68,68,.45)", bg: "rgba(239,68,68,.16)" };
    if (x === "MEDIUM") return { text: "MEDIUM", bd: "rgba(245,158,11,.45)", bg: "rgba(245,158,11,.16)" };
    if (x === "LOW") return { text: "LOW", bd: "rgba(34,197,94,.45)", bg: "rgba(34,197,94,.16)" };
    return { text: esc(level || "—"), bd: "rgba(255,255,255,.18)", bg: "rgba(255,255,255,.06)" };
  };

  const closePopup = () => {
    if (popupRef.current) {
      try {
        popupRef.current.remove();
      } catch {}
      popupRef.current = null;
    }
  };

  const popupHtml = (p) => {
    const fieldName = esc(p?.fieldName || "Поле");
    const cropName = esc(p?.cropName || "—");
    const img = cropImgFromProps(p);
    const risk = toneRisk(p?.riskLevel);

    const areaTxt = Number.isFinite(Number(p?.areaHa)) ? `${fmtFixed(p.areaHa, 1)} ha` : "—";
    const zoneHa = p?.yieldZoneHa != null ? Number(p.yieldZoneHa) : null;
    const yieldTxt = zoneHa != null && Number.isFinite(zoneHa)
      ? `${fmtFixed(zoneHa, 1)} га`
      : (Number.isFinite(Number(p?.yieldValue)) ? fmtFixed(p.yieldValue, 2) : "—");

    const a = p?.activeTasksCount ?? "—";
    const o = p?.overdueTasksCount ?? "—";
    const b = p?.blockedTasksCount ?? "—";

    const modeLabel = mode === "RISK" ? "⚠ Ризики" : mode === "YIELD" ? "📊 Врожайність" : "🌾 Культура";

    return `
      <div class="dashPop">
        <div class="dashCard">
          <div class="dashHead">
            <div class="dashThumb">
              ${img ? `<img src="${img}" alt="${cropName}" onerror="this.style.display='none';" />` : ""}
              <div class="dashThumbFallback">🌾</div>
            </div>

            <div class="dashTitleWrap">
              <div class="dashTitle">${fieldName}</div>
              <div class="dashSub">
                <span class="dashPill">${modeLabel}</span>
                <span class="dashPill">📏 ${areaTxt}</span>
              </div>
            </div>
          </div>

          <div class="dashBody">
            <div class="dashRow">
              <div class="dashStat">
                <div class="k">Культура</div>
                <div class="v">🌾 ${cropName}</div>
              </div>

              <div class="dashStat">
                <div class="k">Врожайність</div>
                <div class="v">📊 ${esc(yieldTxt)}</div>
              </div>
            </div>

            <div class="dashRow">
              <div class="dashStat">
                <div class="k">Ризик</div>
                <div class="v">
                  <span class="dashBadge" style="border-color:${risk.bd}; background:${risk.bg};">⚠ ${risk.text}</span>
                </div>
              </div>

              <div class="dashStat">
                <div class="k">Задачі</div>
                <div class="v dashTasks">
                  <span class="t ok">✅ ${esc(a)}</span>
                  <span class="t warn">⏰ ${esc(o)}</span>
                  <span class="t bad">⛔ ${esc(b)}</span>
                </div>
              </div>
            </div>

            <button class="dashBtn" type="button" data-open="1">Відкрити поле →</button>
          </div>
        </div>
      </div>
    `;
  };

  // ---------- data ----------
  const mapFields = useMemo(() => {
    const arr = Array.isArray(fields) ? fields : [];
    if (showAll) return arr;
    if (!selectedId) return [];
    return arr.filter((f) => String(f?.id ?? f?.fieldId) === String(selectedId));
  }, [fields, showAll, selectedId]);

  const geo = useMemo(() => {
    const polyFeatures = [];
    const centerFeatures = [];

    for (const row of mapFields) {
      const fieldIdRaw = row?.id ?? row?.fieldId;
      if (fieldIdRaw == null) continue;

      const geom = extractGeometry(row);
      if (!geom) continue;

      const props = {
        fieldId: String(fieldIdRaw),
        fieldName: row?.fieldName ?? row?.name ?? `Поле #${fieldIdRaw}`,
        areaHa: row?.areaHa ?? null,
        cropName: row?.cropName ?? null,
        cropId: row?.cropId ?? null,
        cropCategory: row?.cropCategory ?? null,
        yieldValue: row?.yieldValue ?? null,
        yieldZoneHa: row?.yieldZoneHa ?? null,
        riskLevel: row?.riskLevel ?? null,
        riskScore: row?.riskScore ?? null,
        activeTasksCount: row?.activeTasksCount ?? null,
        overdueTasksCount: row?.overdueTasksCount ?? null,
        blockedTasksCount: row?.blockedTasksCount ?? null,
      };

      polyFeatures.push({ type: "Feature", geometry: geom, properties: props });

      const center = computeCenterFromGeom(geom);
      if (center) {
        centerFeatures.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: center },
          properties: props,
        });
      }
    }

    return {
      polys: { type: "FeatureCollection", features: polyFeatures },
      centers: { type: "FeatureCollection", features: centerFeatures },
    };
  }, [mapFields]);

  // ---------- init map ----------
  useEffect(() => {
    if (!mapContainerRef.current) return;
    if (mapRef.current) return;
    if (!token) return;

    mapboxgl.accessToken = token;

    const map = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: SATELLITE_STYLE,
      center: [31.0, 48.5],
      zoom: 5.6,
      attributionControl: false,
    });

    map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), "top-right");

    map.on("load", () => {
      addTerrainLayer(map);
      map.addSource("dash-fields-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });
      map.addSource("dash-centers-src", { type: "geojson", data: { type: "FeatureCollection", features: [] } });

      map.addLayer({
        id: "dash-fields-fill",
        type: "fill",
        source: "dash-fields-src",
        paint: { "fill-color": "rgba(34,197,94,0.20)", "fill-opacity": 0.25 },
      });

      map.addLayer({
        id: "dash-fields-line",
        type: "line",
        source: "dash-fields-src",
        paint: { "line-color": "rgba(255,255,255,0.78)", "line-width": 2.2, "line-opacity": 0.9 },
      });

      map.addLayer({
        id: "dash-centers-circle",
        type: "circle",
        source: "dash-centers-src",
        paint: {
          "circle-radius": 7,
          "circle-color": "rgba(34,197,94,0.98)",
          "circle-stroke-width": 3,
          "circle-stroke-color": "rgba(0,0,0,0.85)",
        },
      });

      map.on("mouseenter", "dash-centers-circle", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "dash-centers-circle", () => (map.getCanvas().style.cursor = ""));
      map.on("mouseenter", "dash-fields-fill", () => (map.getCanvas().style.cursor = "pointer"));
      map.on("mouseleave", "dash-fields-fill", () => (map.getCanvas().style.cursor = ""));

      const openPopupAt = (lngLat, props) => {
        closePopup();

        if (props?.fieldId != null) onSelectRef.current?.(String(props.fieldId));

        const pop = new mapboxgl.Popup({
          closeButton: true,
          closeOnClick: false,
          maxWidth: "360px",
          offset: 14,
          className: "dashPopupWrap",
        })
          .setLngLat(lngLat)
          .setHTML(popupHtml(props))
          .addTo(map);

        // ✅ FIX: bind click immediately after addTo (no "open" event dependency)
        window.setTimeout(() => {
          try {
            const el = pop.getElement?.();
            const btn = el?.querySelector?.('[data-open="1"]');
            if (btn) {
              btn.onclick = (e) => {
                e?.preventDefault?.();
                e?.stopPropagation?.();
                const fid = props?.fieldId != null ? String(props.fieldId) : null;
                if (fid) onOpenFieldRef.current?.(fid);
              };
            }
          } catch {}
        }, 0);

        popupRef.current = pop;
      };

      map.on("click", "dash-centers-circle", (e) => {
        const f = e?.features?.[0];
        if (!f) return;
        openPopupAt(e.lngLat, f.properties || {});
      });

      map.on("click", "dash-fields-fill", (e) => {
        const f = e?.features?.[0];
        if (!f) return;
        const props = f.properties || {};
        const center = computeCenterFromGeom(f.geometry);
        if (center) openPopupAt(center, props);
        else openPopupAt(e.lngLat, props);
      });

      setTimeout(() => {
        try {
          map.resize();
        } catch {}
      }, 150);
    });

    mapRef.current = map;

    return () => {
      try {
        closePopup();
        map.remove();
      } catch {}
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // mode paint
  const applyModeStyle = (map, mode) => {
    if (!map?.getLayer("dash-fields-fill")) return;

    if (mode === "RISK") {
      map.setPaintProperty("dash-fields-fill", "fill-color", [
        "match",
        ["upcase", ["to-string", ["get", "riskLevel"]]],
        "LOW",
        "#22c55e",
        "MEDIUM",
        "#f59e0b",
        "HIGH",
        "#ef4444",
        "#64748b",
      ]);
      map.setPaintProperty("dash-fields-fill", "fill-opacity", 0.25);
      return;
    }

    if (mode === "YIELD") {
      map.setPaintProperty("dash-fields-fill", "fill-color", [
        "interpolate",
        ["linear"],
        ["coalesce", ["to-number", ["get", "yieldValue"]], 0],
        0,
        "#ef4444",
        3,
        "#facc15",
        6,
        "#22c55e",
      ]);
      map.setPaintProperty("dash-fields-fill", "fill-opacity", 0.25);
      return;
    }

    map.setPaintProperty("dash-fields-fill", "fill-color", [
      "match",
      ["upcase", ["to-string", ["get", "cropCategory"]]],
      "GRAINS",      "#fde047",
      "OILSEEDS",    "#f97316",
      "LEGUMES",     "#60a5fa",
      "VEGETABLES",  "#22c55e",
      "FRUITS",      "#f43f5e",
      "INDUSTRIAL",  "#a3e635",
      "FORAGE",      "#34d399",
      "#3b82f6",
    ]);
    map.setPaintProperty("dash-fields-fill", "fill-opacity", 0.20);
  };

  // update data + fit
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const srcFields = map.getSource("dash-fields-src");
    const srcCenters = map.getSource("dash-centers-src");
    if (!srcFields || !srcCenters) return;

    srcFields.setData(geo.polys);
    srcCenters.setData(geo.centers);

    applyModeStyle(map, mode);

    const feats = geo.polys?.features || [];
    if (!feats.length) {
      setTimeout(() => {
        try {
          map.resize();
        } catch {}
      }, 60);
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    let ok = false;

    for (const f of feats) {
      const b = computeBoundsFromGeom(f?.geometry);
      if (!b) continue;
      bounds.extend(b.getSouthWest());
      bounds.extend(b.getNorthEast());
      ok = true;
    }

    if (ok) {
      closePopup();
      map.fitBounds(bounds, { padding: 60, duration: 650, maxZoom: showAll ? 14 : 16 });
    }

    setTimeout(() => {
      try {
        map.resize();
      } catch {}
    }, 60);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geo, mode, showAll]);

  useEffect(() => {
    closePopup();
  }, [selectedId, showAll, mode]);

  return (
    <div style={{ position: "relative", height, width: "100%", overflow: "visible" }}>
      <div
        ref={mapContainerRef}
        style={{
          position: "absolute",
          inset: 0,
          borderRadius: 16,
          overflow: "visible",
          background: "rgba(255,255,255,0.03)",
        }}
      />

      <style>{`
        .mapboxgl-canvas{ border-radius: 16px !important; }
        .mapboxgl-map{ border-radius: 16px !important; }

        .dashPopupWrap .mapboxgl-popup-content{
          background: transparent !important;
          padding: 0 !important;
          box-shadow: none !important;
          max-width: 360px !important;
        }
        .dashPopupWrap .mapboxgl-popup-tip{
          border-top-color: rgba(10,12,11,.92) !important;
          border-bottom-color: rgba(10,12,11,.92) !important;
        }
        .dashPopupWrap .mapboxgl-popup-close-button{
          color: rgba(255,255,255,.92) !important;
          font-size: 18px !important;
          padding: 8px 10px !important;
          right: 4px !important;
          top: 4px !important;
          border-radius: 10px !important;
        }
        .dashPopupWrap .mapboxgl-popup-close-button:hover{
          background: rgba(255,255,255,.08) !important;
        }

        .dashPop{ width: min(360px, calc(100vw - 32px)); font-family: ui-sans-serif, system-ui, -apple-system; color: rgba(255,255,255,.92); }
        .dashCard{
          border-radius: 18px;
          border: 1px solid rgba(255,255,255,.12);
          background: radial-gradient(900px 240px at 0% 0%, rgba(34,197,94,.12), transparent 55%), rgba(10,12,11,.92);
          box-shadow: 0 22px 80px rgba(0,0,0,.65);
          overflow: hidden;
          max-height: min(520px, 62vh);
          display: flex;
          flex-direction: column;
          backdrop-filter: blur(10px);
        }
        .dashHead{ padding: 12px; display:flex; gap:10px; align-items:center; border-bottom: 1px solid rgba(255,255,255,.10); }
        .dashThumb{
          width: 54px; height: 54px; border-radius: 16px;
          border: 1px solid rgba(255,255,255,.12);
          background: rgba(255,255,255,.06);
          overflow:hidden; flex: 0 0 auto;
          position: relative; display:flex; align-items:center; justify-content:center;
        }
        .dashThumb img{ width:100%; height:100%; object-fit:cover; display:block; }
        .dashThumbFallback{ position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-weight:1000; opacity:.85; pointer-events:none; }
        .dashTitleWrap{ min-width:0; flex: 1 1 auto; }
        .dashTitle{ font-weight:1000; font-size:14px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .dashSub{ margin-top: 6px; display:flex; gap:8px; flex-wrap:wrap; }
        .dashPill{ display:inline-flex; align-items:center; gap:6px; padding: 6px 10px; border-radius: 999px; border:1px solid rgba(255,255,255,.14); background: rgba(0,0,0,.22); font-weight:900; font-size:12px; }
        .dashBody{ padding: 12px; display:grid; gap:10px; overflow:auto; }
        .dashRow{ display:grid; grid-template-columns: 1fr 1fr; gap: 10px; }
        @media (max-width: 380px){ .dashRow{ grid-template-columns: 1fr; } }
        .dashStat{ border-radius:14px; border:1px solid rgba(255,255,255,.10); background: rgba(255,255,255,.03); padding: 10px; }
        .dashStat .k{ opacity:.65; font-size:11px; font-weight:950; text-transform:uppercase; letter-spacing:.03em; }
        .dashStat .v{ margin-top:6px; font-size:14px; font-weight:1000; }
        .dashBadge{ display:inline-flex; align-items:center; gap:6px; padding: 6px 10px; border-radius: 999px; border: 1px solid rgba(255,255,255,.18); font-weight:950; font-size:12px; }
        .dashTasks{ display:flex; gap:8px; flex-wrap:wrap; }
        .dashTasks .t{ display:inline-flex; align-items:center; gap:6px; padding: 6px 10px; border-radius: 999px; border: 1px solid rgba(255,255,255,.12); background: rgba(0,0,0,.18); font-weight:950; font-size:12px; }
        .dashTasks .ok{ border-color: rgba(34,197,94,.30); background: rgba(34,197,94,.10); }
        .dashTasks .warn{ border-color: rgba(245,158,11,.30); background: rgba(245,158,11,.10); }
        .dashTasks .bad{ border-color: rgba(239,68,68,.30); background: rgba(239,68,68,.10); }
        .dashBtn{ height: 42px; width: 100%; border-radius: 14px; border: 1px solid rgba(34,197,94,.35); background: rgba(34,197,94,.14); color: rgba(255,255,255,.92); font-weight: 1000; cursor:pointer; }
        .dashBtn:hover{ filter: brightness(1.08); }
      `}</style>
    </div>
  );
}